<template>
  <div class="layout-px-spacing">
    <teleport to="#breadcrumb">
      <ul class="navbar-nav flex-row">
        <li>
          <div class="page-header">
            <nav class="breadcrumb-one" aria-label="breadcrumb">
              <ol class="breadcrumb">
                <li class="breadcrumb-item active" aria-current="page"><span>Dashboard</span></li>
              </ol>
            </nav>
          </div>
        </li>
      </ul>
    </teleport>

    <div class="row layout-top-spacing">
      <div class="col-xl-12 col-lg-12 col-md-12 col-12 layout-spacing">
        <div class="panel br-4">
          <div class="panel-body">
            <h3>Welcome to the Violation Reports!</h3>

            <!-- Loading Property -->
            <div v-if="isLoading" class="text-center p-4">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="mt-2">Loading reports...</p>
            </div>

            <div v-else>
              <!-- Violation Summary Counters -->
              <div class="row mb-4">
                <!-- Total Violations -->
                <div class="col-xl-3 col-lg-6 col-sm-6 mb-4">
                  <div class="stats-card card">
                    <div class="card-body">
                      <div class="d-flex justify-content-between">
                        <div>
                          <h6 class="mb-0">Total Violation</h6>
                          <h3 class="mt-2 mb-0">
                            <count-to :start-val="0" :end-val="totalViolations" :duration="2000" />
                          </h3>
                        </div>
                        <div class="icon-bg">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                            class="feather feather-alert-triangle">
                            <path
                              d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z">
                            </path>
                            <line x1="12" y1="9" x2="12" y2="13"></line>
                            <line x1="12" y1="17" x2="12.01" y2="17"></line>
                          </svg>
                        </div>
                      </div>
                      <p class="mt-3 mb-0 text-muted text-sm">
                        <span class="text-secondary mr-2">
                          <i class="fa fa-arrow-up"></i> Action Events
                        </span>
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Drivers With Violations -->
                <div class="col-xl-3 col-lg-6 col-sm-6 mb-4">
                  <div class="stats-card card">
                    <div class="card-body">
                      <div class="d-flex justify-content-between">
                        <div>
                          <h6 class="mb-0">Violating Drivers</h6>
                          <h3 class="mt-2 mb-0">
                            <count-to :start-val="0" :end-val="violatingDrivers" :duration="2000" />
                          </h3>
                        </div>
                        <div class="icon-bg">
                          <router-link to="/violation/reports/drivers">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                              fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                              stroke-linejoin="round" class="feather feather-users">
                              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                              <circle cx="9" cy="7" r="4"></circle>
                              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                            </svg>
                          </router-link>
                        </div>
                      </div>
                      <p class="mt-3 mb-0 text-muted text-sm">
                        <span class="text-warning mr-2">
                          {{ repeatOffenders }} repeat offenders
                        </span>
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Violation Types -->
                <div class="col-xl-3 col-lg-6 col-sm-6 mb-4">
                  <div class="stats-card card">
                    <div class="card-body">
                      <div class="d-flex justify-content-between">
                        <div>
                          <h6 class="mb-0">Violation Types</h6>
                          <h3 class="mt-2 mb-0">
                            <count-to :start-val="0" :end-val="violationTypes" :duration="2000" />
                          </h3>
                        </div>
                        <router-link to="/violation/parameters/view">
                          <div class="icon-bg">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                              fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                              stroke-linejoin="round" class="feather feather-list">
                              <line x1="8" y1="6" x2="21" y2="6"></line>
                              <line x1="8" y1="12" x2="21" y2="12"></line>
                              <line x1="8" y1="18" x2="21" y2="18"></line>
                              <line x1="3" y1="6" x2="3.01" y2="6"></line>
                              <line x1="3" y1="12" x2="3.01" y2="12"></line>
                              <line x1="3" y1="18" x2="3.01" y2="18"></line>
                            </svg>
                          </div>
                        </router-link>
                      </div>
                      <p class="mt-3 mb-0 text-muted text-sm">
                        <span class="text-info mr-2">
                          {{ mostCommonViolation }}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Recent Violations -->
                <div class="col-xl-3 col-lg-6 col-sm-6 mb-4">
                  <div class="stats-card card">
                    <div class="card-body">
                      <div class="d-flex justify-content-between">
                        <div>
                          <h6 class="mb-0">Recent Violations</h6>
                          <h3 class="mt-2 mb-0">
                            <count-to :start-val="0" :end-val="recentViolations" :duration="2000" />
                          </h3>
                        </div>
                        <div class="icon-bg">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                            class="feather feather-clock">
                            <circle cx="12" cy="12" r="10"></circle>
                            <polyline points="12 6 12 12 16 14"></polyline>
                          </svg>
                        </div>
                      </div>
                      <p class="mt-3 mb-0 text-muted text-sm">
                        <span class="text-success mr-2">
                          Added {{ newViolationsThisWeek }} new this week
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div> <!-- End of Violation Summary Counters -->
            </div> <!-- End of v-else -->
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useMeta } from '@/composables/use-meta';
import { ref, computed, onMounted } from 'vue';
import { CountTo } from 'vue3-count-to';
import axios from 'axios';
import { BASE_URL, BASE_URL_2 } from '/src/Config/config';
import Swal from 'sweetalert2';
import { useStore } from 'vuex';

useMeta({ title: 'Dashboard' });

const isLoading = ref(false);
const violations = ref([]);
const violationActions = ref([]);
const violationParameters = ref([]);
const drivers = ref([]);
const trucks = ref([]);
const trailers = ref([]);
const loading_order_item = ref([]);
const filterStartDate = ref('');
const filterEndDate = ref('');
const selectedViolation = ref(null);
const store = useStore();

// Get username from store
const username = computed(() => {
  const user = store.state.user;
  return user?.username || user?.name || 'User';
});

const showSuccessAlert = (message) => {
  Swal.fire({
    icon: 'success',
    title: 'Success!',
    text: message,
    padding: '2em',
    timer: 3000,
    timerProgressBar: true,
    showConfirmButton: false
  });
};

const showErrorAlert = (message) => {
  Swal.fire({
    icon: 'error',
    title: 'Error!',
    text: message,
    padding: '2em',
    confirmButtonColor: '#3085d6',
    confirmButtonText: 'OK'
  });
};

onMounted(async () => {
  isLoading.value = true;
  try {
    const responses = await Promise.all([
      axios.get(`${BASE_URL}/violations`, {
        headers: {
          'Authorization': `Bearer ${store.state.token}`,
          'X-Company-ID': store.state.currentCompany?.id
        }
      }),
      axios.get(`${BASE_URL}/violation-actions`, {
        headers: {
          'Authorization': `Bearer ${store.state.token}`,
          'X-Company-ID': store.state.currentCompany?.id
        }
      }),
      axios.get(`${BASE_URL}/violation-parameters`, {
        headers: {
          'Authorization': `Bearer ${store.state.token}`,
          'X-Company-ID': store.state.currentCompany?.id
        }
      }),
      axios.get(`${BASE_URL_2}/employee/driver/show`),
      axios.get(`${BASE_URL_2}/vehicle/truck/show`),
      axios.get(`${BASE_URL_2}/vehicle/trailer/show`),
      axios.get(`${BASE_URL_2}/loading-order-item/show`)
    ]);

    // Process responses
    violations.value = responses[0].data.data || responses[0].data;
    violationActions.value = responses[1].data.data || responses[1].data;
    violationParameters.value = responses[2].data.data || responses[2].data;
    drivers.value = responses[3].data.drivers || responses[3].data.data || responses[3].data;
    trucks.value = responses[4].data.trucks || responses[4].data.data || responses[4].data;
    trailers.value = responses[5].data.trailers || responses[5].data.data || responses[5].data;
    loading_order_item.value = responses[6].data.loading_order_items || responses[6].data.data || responses[6].data;

    console.log('Loaded data:', {
      violations: violations.value,
      actions: violationActions.value,
      drivers: drivers.value,
      trucks: trucks.value,
      trailers: trailers.value
    });

  } catch (err) {
    console.error("Failed to load data:", err);
    // Add error message to user
    showErrorAlert(err.response?.data?.message || 'Failed to load data');
  } finally {
    isLoading.value = false;
  }
});

// Computed properties
const totalViolations = computed(() => violationActions.value.length);

const violatingDrivers = computed(() => {
  const drivers = new Set()
  violationActions.value.forEach(action => {
    if (action.employee_id) drivers.add(action.employee_id)
  })
  return drivers.size
})

const repeatOffenders = computed(() => {
  const driverCounts = {}
  violationActions.value.forEach(action => {
    if (action.employee_id) {
      driverCounts[action.employee_id] = (driverCounts[action.employee_id] || 0) + 1
    }
  })
  return Object.values(driverCounts).filter(count => count > 1).length
})

const violationTypes = computed(() => {
  const types = new Set()
  violationActions.value.forEach(action => {
    if (action.violation_id) types.add(action.violation_id)
  })
  return types.size
})

const mostCommonViolation = computed(() => {
  const violationCounts = {}
  violationActions.value.forEach(action => {
    if (action.violation_id) {
      violationCounts[action.violation_id] = (violationCounts[action.violation_id] || 0) + 1
    }
  })
  const mostCommon = Object.entries(violationCounts).sort((a, b) => b[1] - a[1])[0]
  return mostCommon ? `Most common: ${mostCommon[0]}` : 'No data'
})

const newViolationsThisWeek = computed(() => {
  const oneWeekAgo = new Date()
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7)
  return violationActions.value.filter(action => {
    const actionDate = new Date(action.created_at)
    return actionDate >= oneWeekAgo
  }).length
})

const recentViolations = computed(() => {
  const oneMonthAgo = new Date()
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1)
  return violationActions.value.filter(action => {
    const actionDate = new Date(action.created_at)
    return actionDate >= oneMonthAgo
  }).length
})

const violationStats = computed(() => {
  const stats = {};

  violations.value.forEach(violation => {
    // Initialize stats for this violation
    stats[violation.id] = {
      totalCases: 0,
      repeatOffenders: []
    };

    // Group actions by driver for this violation
    const driverMap = {};

    violationActions.value
      .filter(action => {
        // Filter by violation_id first
        if (action.violation_id !== violation.id) return false;

        // Then apply date filter if dates are selected
        if (filterStartDate.value && filterEndDate.value) {
          const actionDate = new Date(action.created_at);
          const start = new Date(filterStartDate.value);
          const end = new Date(filterEndDate.value + 'T23:59:59');
          return actionDate >= start && actionDate <= end;
        }
        return true;
      })
      .forEach(action => {
        stats[violation.id].totalCases++;

        if (!action.employee_id) return;

        if (!driverMap[action.employee_id]) {
          driverMap[action.employee_id] = {
            driverId: action.employee_id,
            count: 0,
            lastDate: '',
            trucks: new Set(),
            trailers: new Set()
          };
        }

        const driverEntry = driverMap[action.employee_id];
        driverEntry.count++;
        if (action.truck_id) driverEntry.trucks.add(action.truck_id);
        if (action.trailer_id) driverEntry.trailers.add(action.trailer_id);

        // Track most recent date
        const actionDate = new Date(action.created_at);
        if (!driverEntry.lastDate || actionDate > new Date(driverEntry.lastDate)) {
          driverEntry.lastDate = action.created_at;
        }
      });

    // Convert to array and filter repeat offenders (count > 1)
    stats[violation.id].repeatOffenders = Object.values(driverMap)
      .map(entry => ({
        ...entry,
        trucks: Array.from(entry.trucks),
        trailers: Array.from(entry.trailers)
      }))
      .filter(entry => entry.count > 0)
      .sort((a, b) => b.count - a.count);
  });

  return stats;
});

const getViolationStats = (violationId) => {
  return violationStats.value[violationId] || {
    totalCases: 0,
    repeatOffenders: []
  };
};

const getParameterName = (paramKey) => {
  const param = violationParameters.value.find(p => p.name === paramKey);
  return param?.label || paramKey;
};

const formatParameterValue = (paramKey, value) => {
  if (value === null || value === undefined) return 'N/A';

  // Special formatting for boolean parameters
  if (value === true) return 'Yes';
  if (value === false) return 'No';

  // Special formatting for night driving
  if (paramKey === 'is_night_driving') {
    return value ? 'Night Driving' : 'Day Driving';
  }

  return value.toString();
};

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  const options = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  };
  return new Date(dateString).toLocaleString(undefined, options);
};

const clearDateFilters = () => {
  filterStartDate.value = '';
  filterEndDate.value = '';
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString();
};

// Apply date filter to modal table
const applyDateFilter = (dateString) => {
  if (!dateString) return '—';
  const options = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  };
  return new Date(dateString).toLocaleString(undefined, options);
};
</script>

<style scoped>
.layout-px-spacing {
  min-height: calc(100vh - 170px) !important;
}

.stats-card {
  border-radius: 8px;
  border: none;
  box-shadow: 0 4px 20px 1px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
}

.stats-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 25px 1px rgba(0, 0, 0, 0.1);
}

.icon-bg {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(94, 114, 228, 0.1);
}

.icon-bg svg {
  width: 24px;
  height: 24px;
  stroke-width: 1.5;
  color: #5e72e4;
}

h6 {
  font-size: 14px;
  color: #6c757d;
  font-weight: 600;
}

h3 {
  font-size: 28px;
  font-weight: 700;
  color: #3c4d69;
}

.text-sm {
  font-size: 12px;
}

.text-success {
  color: #2dce89 !important;
}

.text-warning {
  color: #fb6340 !important;
}

.text-info {
  color: #11cdef !important;
}

.text-danger {
  color: #f5365c !important;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.panel-header h5 {
  margin: 0;
  font-weight: 600;
}

.table th {
  background-color: #f8f9fa;
  font-weight: 600;
}

.table td {
  vertical-align: middle;
}

.badge {
  font-size: 0.85rem;
  padding: 0.35em 0.65em;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.feather {
  width: 16px;
  height: 16px;
  vertical-align: text-bottom;
}
</style>